import { supabase } from './supabase';

export interface QuizCategory {
  id: string;
  name: string;
  min_age: number;
  max_age: number | null;
  created_at: string;
}

export interface QuizQuestion {
  id: string;
  category_id: string;
  question: string;
  options: string[];
  correct_answer: number;
  difficulty: 'easy' | 'medium' | 'hard';
  created_at: string;
  updated_at: string;
}

export interface QuizResult {
  id: string;
  user_id: string;
  category_id: string;
  score: number;
  total_questions: number;
  completed_at: string;
}

/**
 * Busca todas as categorias de quiz
 */
export async function fetchQuizCategories(): Promise<QuizCategory[]> {
  try {
    const { data, error } = await supabase
      .from('quiz_categories')
      .select('*')
      .order('min_age');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching quiz categories:', error);
    return [];
  }
}

/**
 * Busca perguntas para uma categoria e dificuldade específicas
 */
export async function fetchQuizQuestions(
  categoryId: string,
  difficulty: 'easy' | 'medium' | 'hard',
  limit?: number
): Promise<QuizQuestion[]> {
  try {
    let query = supabase
      .from('quiz_questions')
      .select('*')
      .eq('category_id', categoryId)
      .eq('difficulty', difficulty)
      .order('created_at');

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching quiz questions:', error);
    return [];
  }
}

/**
 * Salva o resultado de um quiz
 */
export async function saveQuizResult(
  categoryId: string,
  score: number,
  totalQuestions: number
): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    const { error } = await supabase
      .from('quiz_results')
      .insert({
        user_id: session.user.id,
        category_id: categoryId,
        score,
        total_questions: totalQuestions,
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error saving quiz result:', error);
    return false;
  }
}

/**
 * Busca os resultados de quiz do usuário
 */
export async function fetchUserQuizResults(limit?: number): Promise<QuizResult[]> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return [];

    let query = supabase
      .from('quiz_results')
      .select('*')
      .eq('user_id', session.user.id)
      .order('completed_at', { ascending: false });

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching user quiz results:', error);
    return [];
  }
}

/**
 * Retorna o título do ranking com base na porcentagem de acertos
 */
export function getQuizRankingTitle(percentage: number): string {
  if (percentage >= 90) return 'Mestre Bíblico';
  if (percentage >= 70) return 'Discípulo Dedicado';
  if (percentage >= 50) return 'Aprendiz Promissor';
  return 'Iniciante na Fé';
}

/**
 * Retorna a pontuação por questão com base na dificuldade
 */
export function getPointsPerQuestion(difficulty: 'easy' | 'medium' | 'hard'): number {
  switch (difficulty) {
    case 'easy': return 5;
    case 'medium': return 10;
    case 'hard': return 15;
    default: return 5;
  }
}

/**
 * Retorna o número de questões com base na dificuldade
 */
export function getQuestionCount(difficulty: 'easy' | 'medium' | 'hard'): number {
  switch (difficulty) {
    case 'easy': return 10;
    case 'medium': return 15;
    case 'hard': return 20;
    default: return 10;
  }
}

/**
 * Embaralha um array (algoritmo Fisher-Yates)
 */
export function shuffleArray<T>(array: T[]): T[] {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}